import { defaultMarket, markets } from "../data/markets";
import type { CountryDetection, CountryPreference, Market } from "../types/market";

const LOOKUP_TIMEOUT_MS = 3500;
const COUNTRY_CODE = /^[A-Z]{2}$/;
const PREFERENCE_KEY = "aura-pais-preferencia";
const PREFERENCES: CountryPreference[] = ["auto", "AR", "MX", "CO", "CL", "PE", "INTL"];

/** Devuelve el mercado configurado para un país, o el mercado USD por defecto. */
export function marketForCountry(country: string | null): Market {
  if (!country) return defaultMarket;
  return markets[country] ?? defaultMarket;
}

/**
 * Formatea un importe fijo con su etiqueta de moneda explícita, p. ej. "ARS 27.999" o "USD 19.99".
 * Usa espacio no separable para que la etiqueta y la cifra no se separen en dos líneas.
 */
export function formatAmount(amount: number, market: Market): string {
  const hasCents = !Number.isInteger(amount);
  const options = {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
    useGrouping: "always",
  } as unknown as Intl.NumberFormatOptions;
  const formatted = new Intl.NumberFormat(market.locale, options).format(amount);
  return `${market.currency}\u00A0${formatted}`;
}

/** Suma importes en unidades mínimas (centavos) para evitar errores de coma flotante. */
export function sumAmounts(amounts: number[]): number {
  const cents = amounts.reduce((total, value) => total + Math.round(value * 100), 0);
  return cents / 100;
}

/** Nombre del país en español (p. ej. "MX" → "México"). */
export function countryName(code: string | null): string {
  if (!code) return "";
  try {
    return new Intl.DisplayNames(["es"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/* ---------- Parámetros de prueba en la URL ---------- */

function searchParams(): URLSearchParams | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search);
}

/** SOLO PARA PRUEBAS: lee ?pais=XX de la URL. No cambia el mercado que detecta Impultienda. */
export function countryFromUrl(): string | null {
  const code = searchParams()?.get("pais")?.trim().toUpperCase() ?? "";
  return COUNTRY_CODE.test(code) ? code : null;
}

/** El diagnóstico solo se muestra en pruebas: con ?pais=XX o ?diag=1 en la URL. */
export function diagnosticsEnabled(): boolean {
  const params = searchParams();
  return Boolean(params && (params.has("pais") || params.has("diag")));
}

/** Quita ?pais= de la URL sin recargar, para que la selección manual tome efecto. */
export function clearUrlCountry(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("pais")) return;
  url.searchParams.delete("pais");
  url.searchParams.set("diag", "1");
  window.history.replaceState(null, "", url.toString());
}

/* ---------- Preferencia guardada (solo en este navegador) ---------- */

export function readPreference(): CountryPreference {
  try {
    const stored = window.localStorage.getItem(PREFERENCE_KEY) as CountryPreference | null;
    return stored && PREFERENCES.includes(stored) ? stored : "auto";
  } catch {
    return "auto";
  }
}

export function storePreference(preference: CountryPreference): void {
  try {
    if (preference === "auto") window.localStorage.removeItem(PREFERENCE_KEY);
    else window.localStorage.setItem(PREFERENCE_KEY, preference);
  } catch {
    // Almacenamiento no disponible (p. ej. iframe aislado): la selección vale solo para esta visita.
  }
}

/** Mercado para una preferencia manual. */
export function marketForPreference(preference: Exclude<CountryPreference, "auto">): Market {
  return preference === "INTL" ? defaultMarket : marketForCountry(preference);
}

/* ---------- Detección automática ---------- */

/** Consulta un servicio de país por IP. Solo se lee el código de país; la IP no se usa ni se guarda. */
async function fetchCountry(url: string, field: string, signal: AbortSignal): Promise<string | null> {
  if (signal.aborted) return null;
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  signal.addEventListener("abort", onAbort);
  const timeout = window.setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      credentials: "omit",
      referrerPolicy: "no-referrer",
      cache: "no-store",
    });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    if (typeof data !== "object" || data === null) return null;
    const raw = (data as Record<string, unknown>)[field];
    const country = typeof raw === "string" ? raw.trim().toUpperCase() : "";
    return COUNTRY_CODE.test(country) ? country : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeout);
    signal.removeEventListener("abort", onAbort);
  }
}

/** Región del idioma del navegador (p. ej. "es-AR" → "AR"). Es solo una pista, no una geolocalización. */
export function countryFromNavigator(): string | null {
  if (typeof navigator === "undefined") return null;
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const lang of languages) {
    const region = lang?.split(/[-_]/)[1]?.toUpperCase();
    if (region && COUNTRY_CODE.test(region)) return region;
  }
  return null;
}

/**
 * Orden: country.is → ipapi.co (solo si country.is falla, nunca como segunda opinión)
 * → región del idioma del navegador → sin país (USD).
 */
export async function detectCountry(signal: AbortSignal): Promise<CountryDetection> {
  const primary = await fetchCountry("https://api.country.is/", "country", signal);
  if (primary) return { country: primary, source: "ip-country-is" };

  const secondary = await fetchCountry("https://ipapi.co/json/", "country_code", signal);
  if (secondary) return { country: secondary, source: "ip-ipapi" };

  const language = countryFromNavigator();
  if (language) return { country: language, source: "language" };

  return { country: null, source: "default" };
}
